import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import type { Content } from '@messenger/types'

import {
  alignmentPointsAtom,
  baseLayoutAtom,
  baseLayoutMarginsSelector,
  editorPagesAtom,
} from '@/atoms/_editorAtoms'
import { selectedPageAtom } from '@/atoms/editorAtoms'
import { unsavedChangesAtom } from '@/atoms/_editorAtoms'
import { objectSelector } from '@/atoms/globalAtoms'

import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Input } from '@/components/priority/Input/Input'
import { Text } from '@/components/priority/Text/Text'

import { useSetToast } from '@/hooks/utility/useSetToast'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'

import {
  getGuidelineErrorMessage,
  validateGuidelinePercentage,
} from '@/utils/editor/validateGuideline'

import { trifoldSnapCenters, twoPanelSnapCenters } from './snapPanelCenters'

export const SnapToGuidesDrawer = () => {
  const { t } = useTranslation()
  const isAdmin = useIsAdmin()
  const pages = useRecoilValue(editorPagesAtom)
  const selectedPage = useRecoilValue(selectedPageAtom)
  const baseLayout = useRecoilValue(baseLayoutAtom)
  const baseLayoutMargins = useRecoilValue(baseLayoutMarginsSelector)
  const setAlignmentPointsInPage = useSetRecoilState(
    // @ts-expect-error - @TODO - objectSelector is defined in JS and TypeScript struggles with selectorFamily parameter types
    objectSelector({
      atom: editorPagesAtom,
      property: `[${selectedPage - 1}].product.alignmentPoints`,
    }),
  )
  const setAlignmentPoints = useSetRecoilState(alignmentPointsAtom)
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)
  const [newGuidePosition, setNewGuidePosition] = useState('')
  const addToast = useSetToast()

  const currentPage: Content | undefined = pages?.[selectedPage - 1]
  const currentGuides = useMemo(
    () => currentPage?.product?.alignmentPoints?.vertical || [],
    [currentPage?.product?.alignmentPoints?.vertical],
  )
  const currentHorizontalGuides = useMemo(
    () => currentPage?.product?.alignmentPoints?.horizontal || [],
    [currentPage?.product?.alignmentPoints?.horizontal],
  )
  const productWidth = currentPage?.product?.productSize?.width ?? 1000

  const getGuidePercentage = useCallback(
    (position: number) => {
      if (productWidth <= 0) {
        return 0
      }
      // Calculate percentage and round to 2 decimal places
      const percentage = (position / productWidth) * 100
      return Math.round(percentage * 100) / 100
    },
    [productWidth],
  )

  const addGuide = useCallback(
    (percentage: number) => {
      // Convert percentage to pixel position within the FULL PRODUCT width
      // Guide position = productWidth × percentage (no padding adjustment needed)
      // This ensures percentages are visually accurate across the entire product
      const guideProductWidth = currentPage?.product?.productSize?.width

      if (!guideProductWidth) {
        addToast({
          icon: 'info',
          text: getGuidelineErrorMessage('NO_PRODUCT_WIDTH', t),
        })
        return
      }

      // Store guide positions with fractional precision (no rounding)
      // This prevents micro-jumps caused by rounding errors during drag calculations
      const position = guideProductWidth * (percentage / 100)

      // Check for duplicates
      if (currentGuides.includes(position)) {
        addToast({
          icon: 'info',
          text: getGuidelineErrorMessage('DUPLICATE_GUIDE', t),
        })
        return
      }

      const newGuides = [...currentGuides, position].sort((a, b) => a - b)
      const updatedAlignmentPoints = {
        vertical: newGuides,
        horizontal: currentHorizontalGuides,
      }

      // Update atom for UI
      setAlignmentPoints(updatedAlignmentPoints)

      // Update page data using targeted selector
      setAlignmentPointsInPage(updatedAlignmentPoints)

      setUnsavedChanges(true)
      setNewGuidePosition('')
    },
    [
      addToast,
      currentGuides,
      currentHorizontalGuides,
      currentPage,
      setAlignmentPoints,
      setAlignmentPointsInPage,
      setUnsavedChanges,
      t,
    ],
  )

  const removeGuide = useCallback(
    (position: number) => {
      const newGuides = currentGuides.filter(
        (guide: number) => guide !== position,
      )
      const updatedAlignmentPoints = {
        vertical: newGuides,
        horizontal: currentHorizontalGuides,
      }

      // Update atom for UI
      setAlignmentPoints(updatedAlignmentPoints)

      // Update page data using targeted selector
      setAlignmentPointsInPage(updatedAlignmentPoints)

      setUnsavedChanges(true)
    },
    [
      currentGuides,
      currentHorizontalGuides,
      setAlignmentPoints,
      setAlignmentPointsInPage,
      setUnsavedChanges,
    ],
  )

  const handleAddClick = () => {
    const validationResult = validateGuidelinePercentage(newGuidePosition)

    if (!validationResult.isValid) {
      addToast({
        icon: 'info',
        text: getGuidelineErrorMessage(validationResult.error, t),
      })
      return
    }

    addGuide(validationResult.percentage)
  }

  const addQuickGuide = (percentage: number) => {
    // percentage is already a decimal (0.25 = 25%), convert to whole number percentage
    addGuide(percentage * 100)
  }

  // A trifold offers its page's three panel centers, read from the raw layout
  // at the page number the canvas draws its fold margins with. It has no
  // center margins, so it never gets the two panels either side of one.
  const pageNumber = currentPage?.pageProperties?.pageNumber
  const trifoldCenterPercentages = useMemo(
    () =>
      trifoldSnapCenters(
        baseLayout,
        pageNumber,
        currentPage?.product?.productSize?.width,
      ),
    [baseLayout, pageNumber, currentPage?.product?.productSize?.width],
  )
  const smartCenterPercentages = useMemo(
    () =>
      twoPanelSnapCenters(
        baseLayoutMargins,
        currentPage?.product?.productSize?.width,
      ),
    [baseLayoutMargins, currentPage?.product?.productSize?.width],
  )

  if (!isAdmin) {
    return null
  }

  const [leftPanel, middlePanel, rightPanel] = trifoldCenterPercentages ?? []

  return (
    <Flex column padding="0 12px 12px 12px" gap="16px">
      <Flex
        column
        padding="12px"
        background="paleSecondary"
        radius="4px"
        borderLeft="3px solid"
        borderColor="brand"
      >
        <Text size="12px" weight="600">
          {t('editor.menus.properties.settings.snapToGuides.howItWorksTitle')}
        </Text>
        <Text size="11px">
          {t(
            'editor.menus.properties.settings.snapToGuides.howItWorksDescription',
          )}
        </Text>
      </Flex>

      <Flex column gap="8px">
        <Text size="14px" weight="600">
          {t(
            'editor.menus.properties.settings.snapToGuides.currentGuidesTitle',
            { count: currentGuides.length },
          )}
        </Text>

        {currentGuides.length === 0 ? (
          <Text size="12px" color="gray2">
            {t('editor.menus.properties.settings.snapToGuides.noGuidesYet')}
          </Text>
        ) : (
          <Flex column gap="8px" maxHeight="200px" overflow="auto">
            {currentGuides.map((position: number) => (
              <Flex
                key={position}
                justify="space-between"
                align="center"
                padding="8px 12px"
                background="gray5"
                radius="4px"
              >
                <Text size="13px">
                  {t(
                    'editor.menus.properties.settings.snapToGuides.guidePosition',
                    {
                      position: Math.round(position * 100) / 100,
                      percentage: getGuidePercentage(position),
                    },
                  )}
                </Text>
                <Button
                  primary
                  onClick={() => removeGuide(position)}
                  icon="deleteSolid"
                >
                  {t(
                    'editor.menus.properties.settings.snapToGuides.removeButton',
                  )}
                </Button>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>

      <Flex column gap="8px">
        <Text size="14px" weight="600">
          {t('editor.menus.properties.settings.snapToGuides.addNewGuideTitle')}
        </Text>

        <Flex column gap="8px">
          <Input
            // @ts-expect-error - Input is not typed yet
            type="text"
            placeholder={t(
              'editor.menus.properties.settings.snapToGuides.inputPlaceholder',
            )}
            value={[newGuidePosition, setNewGuidePosition]}
            onEnter={handleAddClick}
          />
          <Button primary onClick={handleAddClick}>
            {t('editor.menus.properties.settings.snapToGuides.addGuideButton')}
          </Button>
        </Flex>
      </Flex>

      <Flex column gap="8px">
        <Text size="14px" weight="600">
          {t('editor.menus.properties.settings.snapToGuides.quickAddTitle')}
        </Text>
        <Text size="11px" color="gray2" margin="0 0 4px 0">
          {t(
            'editor.menus.properties.settings.snapToGuides.quickAddDescription',
          )}
        </Text>
        <Flex gap="8px" wrap="wrap">
          <Button onClick={() => addQuickGuide(0.25)}>25%</Button>
          <Button onClick={() => addQuickGuide(0.33)}>33%</Button>
          <Button onClick={() => addQuickGuide(0.5)}>50%</Button>
          <Button onClick={() => addQuickGuide(0.67)}>67%</Button>
          <Button onClick={() => addQuickGuide(0.75)}>75%</Button>
        </Flex>
      </Flex>

      {smartCenterPercentages && (
        <Flex column gap="8px">
          <Text size="14px" weight="600">
            {t(
              'editor.menus.properties.settings.snapToGuides.smartGuidesTitle',
            )}
          </Text>
          <Text size="11px" color="gray2" margin="0 0 4px 0">
            {t(
              'editor.menus.properties.settings.snapToGuides.smartGuidesDescription',
            )}
          </Text>
          <Flex gap="8px" wrap="wrap">
            <Button
              onClick={() =>
                addQuickGuide(smartCenterPercentages.leftPanelCenter / 100)
              }
            >
              {t(
                'editor.menus.properties.settings.snapToGuides.leftPanelButton',
                {
                  percentage: smartCenterPercentages.leftPanelCenter.toFixed(2),
                },
              )}
            </Button>
            <Button
              onClick={() =>
                addQuickGuide(smartCenterPercentages.rightPanelCenter / 100)
              }
            >
              {t(
                'editor.menus.properties.settings.snapToGuides.rightPanelButton',
                {
                  percentage:
                    smartCenterPercentages.rightPanelCenter.toFixed(2),
                },
              )}
            </Button>
          </Flex>
        </Flex>
      )}

      {leftPanel !== undefined &&
        middlePanel !== undefined &&
        rightPanel !== undefined && (
          <Flex column gap="8px">
            <Text size="14px" weight="600">
              {t(
                'editor.menus.properties.settings.snapToGuides.smartGuidesTitle',
              )}
            </Text>
            <Text size="11px" color="gray2" margin="0 0 4px 0">
              {t(
                'editor.menus.properties.settings.snapToGuides.trifoldSmartGuidesDescription',
              )}
            </Text>
            <Flex gap="8px" wrap="wrap">
              <Button onClick={() => addQuickGuide(leftPanel / 100)}>
                {t(
                  'editor.menus.properties.settings.snapToGuides.leftPanelButton',
                  { percentage: leftPanel.toFixed(2) },
                )}
              </Button>
              <Button onClick={() => addQuickGuide(middlePanel / 100)}>
                {t(
                  'editor.menus.properties.settings.snapToGuides.middlePanelButton',
                  { percentage: middlePanel.toFixed(2) },
                )}
              </Button>
              <Button onClick={() => addQuickGuide(rightPanel / 100)}>
                {t(
                  'editor.menus.properties.settings.snapToGuides.rightPanelButton',
                  { percentage: rightPanel.toFixed(2) },
                )}
              </Button>
            </Flex>
          </Flex>
        )}

      <Flex
        column
        gap="4px"
        padding="12px"
        background="paleSecondary"
        radius="4px"
        borderLeft="3px solid"
        borderColor="brand"
      >
        <Text size="11px" color="gray2">
          {t('editor.menus.properties.settings.snapToGuides.tipText')}
        </Text>
      </Flex>
    </Flex>
  )
}
